import { Button, Input, showToast, Table, type TableColumns, ThreemaIcon, useDefaultSorting } from '@bp/ui-components';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { EyeSymbol } from '../../../../components/AccountForm/EyeSymbol';
import { useConfirm } from '../../../../hooks/useConfirm';
import { useIsDesktop } from '../../../../hooks/useIsDesktop';
import { niceDate } from '../../../../utils/dateCalculations';
import { naturalStringSort } from '../../../../utils/stringHelper';
import { showSuccessSaveToast } from '../../../../utils/toasts';
import { ThreemaAssignModalProps } from './ThreemaAssignModal';

export const ThreemaTableFragment = graphql(`
  fragment ThreemaTable on Query {
    listThreemaCredentials(where: { uuid: $organizationUuid }) {
      password
      username
      usage
      __typename
      credentialId
      userId
      userCsi
      userThreemaId
      userFirstName
      userLastName
      userNickName
      userDepartment
      userCategory
      userVersion
      userLastCheck
      userCreatedAt
    }
    threemas(where: { organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      profilesConnection {
        __typename
        edges {
          __typename
          node {
            uuid
            __typename
            firstName
            lastName
            displayName
            threemaId
          }
          properties {
            __typename
            workCredentialId
            workPass
            workUser
            enabled
            lastSync
          }
        }
      }
    }
  }
`);

const DisconnectThreemasMutationDocument = graphql(`
  mutation DisconnectThreemas($update: ThreemaUpdateInput!, $where: ThreemaWhere!) {
    updateThreemas(update: $update, where: $where) {
      threemas {
        uuid
        __typename
        profiles {
          uuid
          __typename
          email
          firstName
          lastName
          threemaId
          threemaConnection {
            __typename
            edges {
              properties {
                __typename
                workCredentialId
              }
            }
          }
        }
      }
    }
  }
`);

type ThreemaTableType = {
  firstName?: string | null | undefined;
  lastName?: string | null | undefined;
  threemaId?: string | null | undefined;
  uuid: string | undefined;
  enabled?: boolean | null | undefined;
  lastSync?: string;
  password?: string | null | undefined;
  username?: string;
  usage?: number | null | undefined;
  userThreemaId?: string | null | undefined;
  credentialId?: string | null | undefined;
  threemaNodeUuid: string | undefined;
};

type ThreemaTableProps = {
  query: FragmentType<typeof ThreemaTableFragment> | undefined;
  handleAssignClick: (threemaProps: Pick<ThreemaAssignModalProps, 'threemaDetails'>['threemaDetails']) => void;
  refetch: () => void;
};

export const ThreemaTable = ({ query, handleAssignClick, refetch }: ThreemaTableProps) => {
  const { t } = useTranslation();
  const [pwVisible, setPwVisible] = useState<Record<number, boolean>>({});
  const isDesktop = useIsDesktop();

  const fragment = getFragmentData(ThreemaTableFragment, query);
  const threemaProfiles = fragment?.threemas.flatMap(
    (threema) =>
      threema.profilesConnection.edges.map((profile) => ({
        threemaNodeUuid: threema.uuid,
        ...profile.properties,
        ...profile.node,
      })) ?? [],
  );

  const fromThreema: ThreemaTableType[] =
    fragment?.listThreemaCredentials
      .map((threemaCredentials) => {
        const { __typename, ...threemaWithoutTypename } = threemaCredentials;

        const { firstName, lastName, threemaId, enabled, lastSync, uuid, threemaNodeUuid } =
          threemaProfiles?.find((threema) => threema.workCredentialId === threemaCredentials.credentialId) ?? {};

        return {
          ...threemaWithoutTypename,
          threemaId,
          firstName,
          lastName,
          enabled,
          lastSync,
          uuid,
          threemaNodeUuid,
        };
      })
      .sort((a, b) => naturalStringSort(a.lastName ?? '', b.lastName ?? '')) ?? [];

  const columns: TableColumns<ThreemaTableType>[] = [
    {
      header: t('common.firstName'),
      id: 'firstName',
      accessorKey: 'firstName',
    },
    {
      header: t('common.lastName'),
      id: 'lastName',
      accessorKey: 'lastName',
    },
    {
      header: t('threema.enabled'),
      id: 'enabled',
      accessorKey: 'enabled',
      type: 'boolean',
      enableColumnFilter: false,
    },
    {
      header: t('threema.user'),
      id: 'username',
      accessorKey: 'username',
    },
    {
      id: 'password',
      accessorKey: 'password',
      header: t('threema.passwort'),
      size: 150,
      accessorFn: () => null, // not searchable
      cell: (cell) => {
        return (
          <Input
            name={'currentPassword'}
            value={cell.row.original.password}
            readonly={true}
            type={pwVisible[cell.row.index] ? 'text' : 'password'}
            suffix={
              <EyeSymbol
                visible={pwVisible[cell.row.index]}
                toggle={() => setPwVisible((prev) => ({ ...prev, [cell.row.index]: !prev[cell.row.index] }))}
              />
            }
          />
        );
      },
    },
    {
      header: 'ThreemaId',
      id: 'threemaId',
      accessorKey: 'threemaId',
    },
    {
      header: 'LastSync',
      id: 'lastSync',
      accessorKey: 'lastSync',
      accessorFn: (row) => niceDate(row.lastSync),
      cell: (cell) => niceDate(cell.row.original.lastSync, 'short', 'short'),
    },
  ];

  const [, mutation] = useMutation(DisconnectThreemasMutationDocument);
  const { confirm, ConfirmationDialog } = useConfirm({
    defaultTitle: t('threema.disconnect.title'),
    defaultMessage: t('threema.disconnect.message', {
      name: '',
    }),
    defaultConfirmText: t('threema.disconnect.confirm'),
  });
  const handleDisconnect = async (row: ThreemaTableType) => {
    const res = await confirm({
      message: t('threema.disconnect.message', {
        name: row.username,
      }),
    });

    if (res && row.uuid)
      try {
        await mutation({
          where: { uuid: row.threemaNodeUuid },
          update: {
            profiles: [{ disconnect: [{ where: { node: { uuid: row.uuid } } }] }],
          },
        });
        refetch();
        showSuccessSaveToast();
      } catch (error) {
        console.error(error);
        showToast(t('threema.assign_error'), {
          type: 'error',
        });
      }
  };

  const { sorting, onSortingChange } = useDefaultSorting();

  return (
    <>
      <Table<ThreemaTableType>
        isDesktop={isDesktop}
        data-cy='threemaList'
        showActionBar
        isOnWhite={false}
        showBorderRadius
        showShadow
        showSort
        canScroll
        sorting={sorting}
        onSortingChange={onSortingChange}
        actionBarSettings={{
          showExpertFilter: true,
        }}
        emptyStateSettings={{
          icon: <ThreemaIcon />,
          padding: 'xl',
        }}
        columns={columns}
        data={[...fromThreema]}
        lastColWidth='150px'
        lastCol={(row) => {
          const { credentialId, username: workUser, password: workPass, uuid } = row.original;

          if (uuid)
            return (
              <Button hierarchy='primary' onClick={() => handleDisconnect(row.original)}>
                {t('threema.disconnect.title')}
              </Button>
            );

          if (workPass)
            return (
              <Button
                hierarchy='primary'
                onClick={() =>
                  handleAssignClick({
                    enabled: true,
                    workUser,
                    workPass,
                    workCredentialId: credentialId,
                    originalProfileUuid: row.original.uuid ?? null,
                  })
                }
              >
                {t('threema.assign_title')}
              </Button>
            );
        }}
      />
      <ConfirmationDialog />
    </>
  );
};
